import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Space, Spin, Button, Alert } from "antd";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import { calculateDistance } from "../MappaClienti/utils";
import { fetchDocIdsByContent } from "./api";
import { DEFAULT_FEATURES } from "./features";
import { filterDocs, getSelectedDocs, pruneSelectionToDocs, sortDocs } from "./utils";
import DocumentsFilters from "./DocumentsFilters";
import DocumentsGrid from "./DocumentsGrid";
import MapFilterModal from "./MapFilterModal";
import CopyToOrgModal from "./CopyToOrgModal";
import DocumentDrawer from "./DocumentDrawer";

const defaultFilters = {
  nome: "",
  org: null, // objectId org | null
  formato: "all", // "all" | "pdf" | "html"
  contentIds: null, // Set<string> | null
  map: null, // { center: {lat,lng}, rangeKm } | null
};

// Orchestratore riutilizzabile: riceve i dati dall'esterno e attiva/disattiva i pezzi
// UI via `features`. Usato sia da /documents (tutto on) sia dalla vista org (on ridotto).
const DocumentsExplorer = ({
  docs,
  loading,
  error,
  features,
  actions,
  renderHeader,
  regioni = [],
  province = [],
  showOrgLink = true,
  showFlags = false,
  emptyText = "Nessun documento corrisponde ai filtri",
}) => {
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const f = useMemo(() => ({ ...DEFAULT_FEATURES, ...(features || {}) }), [features]);

  const [filters, setFilters] = useState(defaultFilters);
  const [contentSearch, setContentSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [mapVisible, setMapVisible] = useState(false);
  const [copyVisible, setCopyVisible] = useState(false);
  const [cardsPerRow, setCardsPerRow] = useState(4);
  const [showPreview, setShowPreview] = useState(true);
  const [sortBy, setSortBy] = useState("updated_desc");
  const [drawerDoc, setDrawerDoc] = useState(null);

  // Punti org (con coordinate) + numero di documenti per org, per la mappa.
  const orgPoints = useMemo(() => {
    const byOrg = new Map();
    docs.forEach((d) => {
      const org = d.org_pointer;
      const orgId = org && org.objectId;
      if (!orgId || d.orgLat == null || d.orgLng == null) return;
      const existing = byOrg.get(orgId);
      if (existing) existing.count += 1;
      else byOrg.set(orgId, { objectId: orgId, nome: org.nome, lat: d.orgLat, lng: d.orgLng, count: 1 });
    });
    return Array.from(byOrg.values());
  }, [docs]);

  // Organizzazioni presenti nei documenti, per il filtro Org.
  const orgOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    docs.forEach((d) => {
      const org = d.org_pointer;
      if (org && org.objectId && !seen.has(org.objectId)) {
        seen.add(org.objectId);
        opts.push({ value: org.objectId, label: org.nome || org.objectId });
      }
    });
    return opts.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [docs]);

  // Full-text server-side con debounce (min 3 caratteri). Disattivato se features.fullText è off.
  useEffect(() => {
    if (!f.fullText) return undefined;
    const term = contentSearch.trim();
    if (term.length < 3) {
      setFilters((ff) => (ff.contentIds === null ? ff : { ...ff, contentIds: null }));
      return undefined;
    }
    const handle = setTimeout(async () => {
      try {
        const token = await getTokenSilently();
        const ids = await fetchDocIdsByContent(token, env, term);
        setFilters((ff) => ({ ...ff, contentIds: new Set(ids) }));
      } catch (e) {
        console.error("Errore full-text:", e);
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [contentSearch, getTokenSilently, env, f.fullText]);

  const filteredDocs = useMemo(() => filterDocs(docs, filters, calculateDistance), [docs, filters]);
  const sortedDocs = useMemo(() => sortDocs(filteredDocs, sortBy), [filteredDocs, sortBy]);

  useEffect(() => {
    setSelected((prev) => pruneSelectionToDocs(prev, filteredDocs));
  }, [filteredDocs]);

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = () => setSelected(new Set());

  const selectedDocs = useMemo(() => getSelectedDocs(sortedDocs, selected), [sortedDocs, selected]);
  const selectedCount = f.copy ? selectedDocs.length : 0;

  return (
    <div style={{ paddingBottom: selectedCount > 0 ? 72 : 0 }}>
      {renderHeader && renderHeader(filteredDocs.length)}

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}><Spin size="large" /></div>
      ) : (
        <>
          <DocumentsFilters
            features={f}
            filters={filters}
            setFilters={setFilters}
            contentSearch={contentSearch}
            setContentSearch={setContentSearch}
            orgOptions={orgOptions}
            onOpenMap={() => setMapVisible(true)}
            mapActive={!!filters.map}
            onClearMap={() => setFilters((ff) => ({ ...ff, map: null }))}
            cardsPerRow={cardsPerRow}
            setCardsPerRow={setCardsPerRow}
            showPreview={showPreview}
            setShowPreview={setShowPreview}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />

          <DocumentsGrid
            docs={sortedDocs}
            selected={selected}
            onToggleSelect={toggleSelect}
            onOpen={setDrawerDoc}
            cardsPerRow={cardsPerRow}
            showPreview={showPreview}
            selectable={f.copy}
            showOrgLink={showOrgLink}
            showFlags={showFlags}
            emptyDescription={docs.length === 0 ? emptyText : "Nessun documento corrisponde ai filtri"}
          />
        </>
      )}

      {/* Barra azioni sticky (solo con selezione/copia attive) */}
      {f.copy && selectedCount > 0 && (
        <div
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000,
            background: "#fff", borderTop: "1px solid #f0f0f0",
            padding: "12px 24px", boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <Space>
            <strong>{selectedCount}</strong> selezionati
            <Button type="primary" onClick={() => setCopyVisible(true)}>Copia verso org…</Button>
            <Button onClick={clearSelection}>Deseleziona tutto</Button>
          </Space>
        </div>
      )}

      {f.map && (
        <MapFilterModal
          visible={mapVisible}
          onCancel={() => setMapVisible(false)}
          orgPoints={orgPoints}
          initial={filters.map}
          regioni={regioni}
          province={province}
          onApply={(map) => {
            setFilters((ff) => ({ ...ff, map }));
            setMapVisible(false);
          }}
        />
      )}

      {f.copy && (
        <CopyToOrgModal
          visible={copyVisible}
          onCancel={() => setCopyVisible(false)}
          selectedDocs={selectedDocs}
          onDone={() => {
            setCopyVisible(false);
            clearSelection();
          }}
        />
      )}

      <DocumentDrawer
        doc={drawerDoc}
        onClose={() => setDrawerDoc(null)}
        actions={drawerDoc && actions ? actions(drawerDoc, { close: () => setDrawerDoc(null) }) : null}
      />
    </div>
  );
};

export default DocumentsExplorer;
